/**
 * Solutions Overview Page - Customer-Facing Version
 * Public page showcasing AskIsaac's full offerings
 * Can be used as a recap after meetings/demos
 * Each section can be linked directly via URL hash (e.g., /solutions#hart)
 */
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  ChevronDown,
  ChevronRight,
  Target,
  CheckCircle2,
  DollarSign,
  Users,
  Building2,
  Phone,
  Mail,
  Calendar,
  Zap,
  Shield,
  TrendingUp,
  Clock,
  Truck,
  Wrench,
  Package,
  ArrowLeft,
  ArrowRight,
  FileText,
  Star,
  Play,
  ExternalLink,
  Droplets,
  FlaskConical,
  Activity,
  Award,
  MapPin,
  Settings,
  Gauge,
  Microscope,
  BadgeCheck,
  Factory,
  CircleCheck,
  Layers,
  BarChart3,
  Download,
  Lock,
  Smartphone,
  Database,
  Sparkles,
  Heart,
  Globe,
  Cpu,
  Bot,
  LineChart,
  Anchor,
  Home,
  ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BackToHome from "@/components/BackToHome";
import SiteHeader from "@/components/SiteHeader";

const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const STERILEMATE_LOGO = "/logos/sterilemate-white.png?v=4";
// Healthcare-with-workers hero — clinical team in critical environment
const HERO_IMG = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1900";

// Expandable Section Component with anchor support
const Section = ({ id, title, icon: Icon, children, defaultOpen = false, color = "blue" }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const location = useLocation();
  
  // Auto-open section if URL hash matches
  useEffect(() => {
    if (location.hash === `#${id}`) {
      setIsOpen(true);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location.hash, id]);
  
  const colorClasses = {
    blue: "from-blue-500 to-cyan-500",
    green: "from-emerald-500 to-teal-500",
    purple: "from-violet-500 to-purple-500",
    orange: "from-orange-500 to-amber-500",
    red: "from-red-500 to-rose-500",
    cyan: "from-cyan-500 to-sky-500"
  };
  
  return (
    <div id={id} className="rounded-2xl overflow-hidden mb-4 scroll-mt-24 border border-slate-200 bg-white shadow-sm hover:border-blue-400 transition-colors" data-testid={`section-wrapper-${id}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
        data-testid={`section-${id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${colorClasses[color]} flex items-center justify-center shadow-lg shadow-black/20`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-lg">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 text-blue-700" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
      </button>
      {isOpen && (
        <div className="p-6 bg-slate-900/95 border-t border-slate-800 text-slate-100">
          {children}
        </div>
      )}
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, color = "blue" }) => {
  const ringColors = {
    blue:   "border-cyan-400/25 hover:border-cyan-400/50",
    green:  "border-emerald-400/25 hover:border-emerald-400/50",
    purple: "border-violet-400/25 hover:border-violet-400/50",
    orange: "border-amber-400/25 hover:border-amber-400/50",
    cyan:   "border-cyan-400/25 hover:border-cyan-400/50",
  };
  const iconColors = {
    blue:   "bg-gradient-to-br from-blue-500 to-cyan-500",
    green:  "bg-gradient-to-br from-emerald-500 to-teal-500",
    purple: "bg-gradient-to-br from-violet-500 to-purple-500",
    orange: "bg-gradient-to-br from-amber-500 to-orange-500",
    cyan:   "bg-gradient-to-br from-cyan-500 to-sky-500",
  };

  return (
    <div className={`rounded-xl p-5 border bg-slate-900/55 backdrop-blur-md transition-all hover:-translate-y-0.5 ${ringColors[color]}`}>
      <div className={`w-12 h-12 ${iconColors[color]} rounded-lg flex items-center justify-center mb-3 shadow-lg shadow-black/20`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h4 className="font-bold text-white mb-2">{title}</h4>
      <p className="text-sm text-slate-300">{description}</p>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ value, label, icon: Icon }) => (
  <div className="rounded-xl p-4 border border-white/[0.08] bg-slate-900/55 backdrop-blur-md text-center transition-all hover:border-cyan-400/40">
    <Icon className="w-8 h-8 text-cyan-300 mx-auto mb-2" />
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-sm text-slate-400">{label}</p>
  </div>
);

// Quick Navigation Component
const SolutionTiles = () => {
  const tiles = [
    { id: "hart",         label: "H.E.A.R.T Software",           subtitle: "Rebate recovery, bill-only implants, live tier alerts",   icon: Database,     color: "purple", href: "/solutions/heart" },
    { id: "bioburden",    label: "Bioburden Deep-Disinfection", subtitle: "Under-the-hood decon on OR tables, ICU beds & stretchers", icon: ShieldCheck, color: "rose", href: "/services/bioburden" },
    { id: "contracting",  label: "Healthcare General Contracting", subtitle: "Turn-key clinic build-outs and capital projects",     icon: Wrench,       color: "amber", href: "/solutions/general-contracting" },
    { id: "maintenance",  label: "Maintenance & Service",       subtitle: "Preventive maintenance, work orders, SLA-backed",        icon: Wrench,       color: "orange", href: "/hospital/capabilities" },
    { id: "water",        label: "Water Systems",               subtitle: "AAMI ST108 ready · dialysis & sterile processing",       icon: Droplets,     color: "cyan", href: "/solutions/water-treatment" },
    { id: "testing",      label: "Microbiology Testing",        subtitle: "Water testing, environmental cultures, lab partnerships",icon: FlaskConical, color: "emerald", href: "/microbiology-testing" },
    { id: "compliance",   label: "Compliance & Documentation",  subtitle: "OIG · SAM.gov · OFAC · Joint Commission ready",          icon: Shield,       color: "rose", href: "/hospital/capabilities" },
    { id: "security",     label: "Security & Privacy",          subtitle: "HIPAA-aligned, RBAC, zero-trust admin console",          icon: Lock,         color: "slate", href: "/security-whitepaper" },
  ];
  // Semantic color palette — kept static so Tailwind's JIT can safelist.
  const styles = {
    blue:    { ring: "ring-blue-200",    hover: "hover:ring-blue-500",    bar: "bg-blue-500",    icon: "text-blue-700",    bg: "bg-blue-50"    },
    purple:  { ring: "ring-violet-200",  hover: "hover:ring-violet-500",  bar: "bg-violet-500",  icon: "text-violet-700",  bg: "bg-violet-50"  },
    amber:   { ring: "ring-amber-200",   hover: "hover:ring-amber-500",   bar: "bg-amber-500",   icon: "text-amber-700",   bg: "bg-amber-50"   },
    orange:  { ring: "ring-orange-200",  hover: "hover:ring-orange-500",  bar: "bg-orange-500",  icon: "text-orange-700",  bg: "bg-orange-50"  },
    cyan:    { ring: "ring-cyan-200",    hover: "hover:ring-cyan-500",    bar: "bg-cyan-500",    icon: "text-cyan-700",    bg: "bg-cyan-50"    },
    emerald: { ring: "ring-emerald-200", hover: "hover:ring-emerald-500", bar: "bg-emerald-500", icon: "text-emerald-700", bg: "bg-emerald-50" },
    rose:    { ring: "ring-rose-200",    hover: "hover:ring-rose-500",    bar: "bg-rose-500",    icon: "text-rose-700",    bg: "bg-rose-50"    },
    slate:   { ring: "ring-slate-200",   hover: "hover:ring-slate-500",   bar: "bg-slate-500",   icon: "text-slate-700",   bg: "bg-slate-50"   },
  };

  return (
    <div className="mb-10">
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-700 mb-3">Jump To A Solution</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {tiles.map((t) => {
          const s = styles[t.color] || styles.slate;
          const Icon = t.icon;
          return (
            <a
              key={t.id}
              href={t.href || `#${t.id}`}
              className={`group relative overflow-hidden rounded-xl bg-white ring-1 ${s.ring} ${s.hover} p-4 shadow-sm hover:shadow-lg transition-all`}
              data-testid={`solution-tile-${t.id}`}
            >
              <span className={`absolute inset-y-0 left-0 w-1 ${s.bar}`} />
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${s.bg} mb-3`}>
                <Icon className={`w-5 h-5 ${s.icon}`} />
              </div>
              <p className="font-extrabold text-sm text-slate-900 leading-tight mb-1">{t.label}</p>
              <p className="text-[11px] leading-snug text-slate-500">{t.subtitle}</p>
              <span className={`absolute right-3 bottom-3 text-[11px] font-bold ${s.icon} opacity-0 group-hover:opacity-100 transition-opacity`}>Explore →</span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default function SolutionsOverviewPage() {
  const location = useLocation();
  
  // Scroll to hash on page load
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white text-slate-900 relative" data-testid="solutions-overview">
      <SEO
        canonical="/solutions"
        title="Solutions: Sterile Processing, EVS, Water, Supply Chain — Sterile Mate"
        description="One platform for hospital sterile processing, EVS/janitorial, water purification, supply chain, and skilled trades. AAMI ST108-ready, HIPAA-aligned, non-GPO. Serving Texas, Louisiana, Alabama, Mississippi."
        keywords={[
          "AAMI ST108 platform",
          "hospital sterile processing software",
          "healthcare EVS software",
          "hospital water treatment platform",
          "IMS HART software",
          "HITRUST vendor management",
          "hospital compliance heatmap",
        ]}
      />

      {/* ============ SHARED SITE HEADER ============ */}
      <SiteHeader />

      {/* ============ HERO (full-bleed healthcare scene with workers) ============ */}
      <section className="relative pt-10 md:pt-14 pb-14 md:pb-20 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Healthcare team working in critical environment"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          {/* Multilayered overlay so headline is readable + dawn DNA preserved */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d2042]/55 via-[#0d2042]/72 to-[#0d2042]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2042]/55 via-transparent to-[#0d2042]/30" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 0%, rgba(125,211,252,0.30) 0%, rgba(125,211,252,0.12) 40%, rgba(252,211,77,0.05) 65%, transparent 80%)",
            }}
          />
        </div>

        {/* Hero content */}
        <div className="relative max-w-5xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/15 backdrop-blur-md border border-cyan-400/30 text-cyan-200 text-[10px] font-bold uppercase tracking-[0.22em] mb-5">
              <Sparkles className="w-3 h-3" />
              Front-Line Command for Critical Environments
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tight leading-[1.05] drop-shadow-2xl mb-4">
              One platform.<br />Everything you need.
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl drop-shadow mb-7">
              <span className="text-cyan-300 font-semibold">Supplies.</span>{" "}
              <span className="text-emerald-300 font-semibold">Maintenance.</span>{" "}
              <span className="text-amber-300 font-semibold">Software.</span>{" "}
              <span className="text-violet-300 font-semibold">Compliance.</span>{" "}
              The complete healthcare operations partner — running quietly behind every supply order, service call, and project.
            </p>

            {/* Compliance trust strip */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { label: "OIG / SAM.gov / OFAC", color: "text-emerald-300", dot: "bg-emerald-400" },
                { label: "HIPAA-aligned", color: "text-cyan-300", dot: "bg-cyan-400" },
                { label: "AAMI ST108 Ready", color: "text-amber-300", dot: "bg-amber-400" },
                { label: "Joint Commission", color: "text-violet-300", dot: "bg-violet-400" },
              ].map((c) => (
                <span
                  key={c.label}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900/65 backdrop-blur-md border border-white/[0.08] text-[10px] font-bold uppercase tracking-wider"
                >
                  <span className={`w-1 h-1 rounded-full ${c.dot}`} />
                  <span className={c.color}>{c.label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Amber horizon stripe at bottom of hero — matches homepage signature */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-transparent via-amber-400/12 to-transparent pointer-events-none blur-sm" />
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        
        {/* Jump-to-a-Solution tile grid (replaces the old pill-style QuickNav) */}
        <SolutionTiles />
        


        {/* CTA Section */}
        <div className="relative overflow-hidden rounded-3xl p-10 mt-10 border border-cyan-400/25 bg-gradient-to-br from-[#0d2042] via-[#142a4d] to-[#0a1c3c]">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(125,211,252,0.18) 0%, rgba(252,211,77,0.06) 50%, transparent 80%)" }} />
          <div className="relative text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 text-[10px] font-bold uppercase tracking-[0.2em] mb-5">
              <Sparkles className="w-3 h-3" />
              Talk to Operations
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-3">Ready to Transform Your Operations?</h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Schedule a personalized demo to see how AskIsaac can help your facility.
              We&apos;ll walk through your specific needs and show you exactly how our platform delivers results.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/hospital/register">
                <Button size="lg" className="bg-amber-500/10 backdrop-blur-md0 hover:bg-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/20">
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Demo
                </Button>
              </Link>
              <a href="tel:2814563458">
                <Button size="lg" variant="outline" className="border-cyan-400/40 text-cyan-100 hover:bg-cyan-400/10 hover:text-white">
                  <Phone className="w-5 h-5 mr-2" />
                  (281) 456-3458
                </Button>
              </a>
              <a href="mailto:info@sterilemate.com">
                <Button size="lg" variant="outline" className="border-white/20 text-slate-100 hover:bg-slate-900/55 backdrop-blur-md/10">
                  <Mail className="w-5 h-5 mr-2" />
                  info@sterilemate.com
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-10 text-center text-slate-400 text-xs">
          <p>AskIsaac Inc. | Powered by Sterile Mate LLC</p>
          <p>396 W Greens Road, Suite 1139, Houston, TX 77067</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-[#060f1f] border-t border-white/[0.05] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-xs tracking-wider">
            AskIsaac Inc. · Powered by Sterile Mate LLC · 800.988.3892 · info@sterilemate.com
          </p>
        </div>
      </footer>
    </div>
  );
}

// Section hero image — cinematic backdrop with dark gradient overlay
const SectionHero = ({ src, alt, accent = "cyan", tagline, headline }) => {
  const accentMap = {
    cyan:    { ring: "ring-cyan-400/20",    text: "text-cyan-200",    bg: "bg-cyan-500/15" },
    emerald: { ring: "ring-emerald-400/20", text: "text-emerald-200", bg: "bg-emerald-500/15" },
    amber:   { ring: "ring-amber-400/20",   text: "text-amber-200",   bg: "bg-amber-500/15" },
    violet:  { ring: "ring-violet-400/20",  text: "text-violet-200",  bg: "bg-violet-500/15" },
    rose:    { ring: "ring-rose-400/20",    text: "text-rose-200",    bg: "bg-rose-500/15" },
  };
  const t = accentMap[accent] || accentMap.cyan;
  return (
    <div className={`relative w-full h-44 md:h-56 rounded-2xl overflow-hidden ring-1 ${t.ring} mb-5 group`}>
      <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d2042] via-[#0d2042]/55 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        {tagline && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${t.bg} ${t.text} text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-md mb-2 border border-white/[0.06]`}>
            {tagline}
          </span>
        )}
        {headline && <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight drop-shadow-lg">{headline}</h3>}
      </div>
    </div>
  );
};

// Badge component for inline use
const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
    {children}
  </span>
);
